<template>
    <div class="space-y-6">
        <div class="flex flex-col gap-6">
            <div class="flex flex-wrap items-center justify-between gap-4">
                <div class="flex items-center gap-3">
                    <h3 class="text-xl font-bold text-slate-800">Offices ({{ filteredOffices.length }})</h3>
                </div>
            </div>
            <div class="flex items-center justify-between gap-3 w-full">
                <div class="flex gap-4">
                    <Search :value="searchTerm" placeholder="Search by name and email..."
                        @change="searchTerm = $event" />
                </div>
                <div class="flex items-center gap-2">
                    <label for="users-active-toggle" class="inline-flex cursor-pointer items-center users-toggle">
                        <span class="sr-only">Toggle show only active offices</span>
                        <input id="users-active-toggle" v-model="showActiveOnly" type="checkbox"
                            class="users-toggle__input" />
                        <span class="users-toggle__track">
                            <span class="users-toggle__thumb"></span>
                        </span>
                    </label>
                    <span class="text-sm font-medium text-slate-600">Show only active offices</span>
                </div>
            </div>
            <div v-if="loading" class="text-center py-8">
                <div class="inline-flex items-center gap-2 text-slate-500">
                    <svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                        </circle>
                        <path class="opacity-75" fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                        </path>
                    </svg>
                    Loading offices...
                </div>
            </div>
            <div v-else-if="error" class="text-center py-8">
                <div class="text-red-600">
                    {{ error }}
                </div>
            </div>
            <div v-else-if="filteredOffices.length > 0" class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                <article v-for="office in filteredOffices" :key="office.id" :class="cardClass()">
                    <span class="absolute inset-x-0 top-0 h-1 rounded-t" :class="cardAccentClass(office.status)"></span>
                    <div class="flex items-start justify-between pt-3">
                        <div class="space-y-2 text-xs text-slate-500">
                            <p class="text-base font-semibold text-slate-900">
                                {{ office.name }}
                            </p>
                            <div class="flex items-start gap-2 text-slate-600">
                                <Icon icon-name="LocationIcon" />
                                <span class="leading-snug">{{ office.address }}</span>
                            </div>
                            <div class="flex items-center gap-2 text-slate-600">
                                <Icon icon-name="MailIcon" />
                                <span>{{ office.email }}</span>
                            </div>
                            <div class="flex items-center gap-2 text-slate-600">
                                <Icon icon-name="CallIcon" />
                                <span>{{ office.phone }}</span>
                            </div>
                        </div>
                        <span :class="statusBadgeClass(office.status)" :style="statusBadgeStyle(office.status)">
                            <span :class="statusDotClass(office.status)"></span>
                            <span class="capitalize">{{ office.status }}</span>
                        </span>
                    </div>
                    <div class="mt-4 grid grid-cols-3 divide-x divide-white text-center">
                        <div class="space-y-1">
                            <p class="text-lg font-semibold text-slate-900">
                                {{ office.stats.totalUsers }}
                            </p>
                            <p class="text-xs text-slate-500">No. of Users</p>
                        </div>
                        <div class="space-y-1">
                            <p class="text-lg font-semibold text-slate-900">
                                {{ office.stats.primaryUsers }}
                            </p>
                            <p class="text-xs text-slate-500">Primary User</p>
                        </div>
                        <div class="space-y-1">
                            <p class="text-lg font-semibold text-slate-900">
                                {{ office.stats.sharedUsers }}
                            </p>
                            <p class="text-xs text-slate-500">Shared User</p>
                        </div>
                    </div>
                </article>
            </div>
            <EmptyState v-else title="Sorry no offices was found"
                message="We could not find any offices related to this tenant." />
        </div>
    </div>
</template>


<script>
import { mapState } from "vuex";
import Search from "../core/Search.vue";
import Icon from "../core/Icon.vue";
import EmptyState from "../Forms/EmptyState.vue";


export default {
    name: "OfficeTab",
    components: {
        Search,
        Icon,
        EmptyState
    },
    props: {
        tenantId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            searchTerm: "",
            showActiveOnly: false,
            offices: [],
            loading: false,
            error: null
        };
    },
    computed: {
        ...mapState("TenantAnalyticsStore", {
            storedTenantBranches: (state) => state.tenantBranches,
        }),
        filteredOffices() {
            return this.offices.filter(office => {
                const matchesSearch = this.searchTerm
                    ? [office.name, office.email].some(field =>
                        (field || "").toLowerCase().includes(this.searchTerm.toLowerCase())
                    )
                    : true;
                const matchesStatus = this.showActiveOnly
                    ? !!office.isActive
                    : true;
                return matchesSearch && matchesStatus;
            });
        }
    },
    methods: {
        async fetchBranches() {
            this.loading = true;
            this.error = null;
            try {
                if (!this.tenantId) {
                    throw new Error("Tenant ID is required to load offices");
                }

                let branches = this.storedTenantBranches;

                if (!Array.isArray(branches) || branches.length === 0) {
                    branches = await this.$store.dispatch(
                        "TenantAnalyticsStore/fetchTenantBranches",
                        this.tenantId
                    );
                }

                const branchList = Array.isArray(branches?.data) ? branches.data : branches;
                this.offices = this.mapOfficesData(branchList || []);
            } catch (error) {
                this.error = "Failed to load offices";
                this.offices = [];
            } finally {
                this.loading = false;
            }
        },
        mapOfficesData(apiBranches) {
            return apiBranches.map((branch, index) => {
                const addressParts = [branch.street, branch.city, branch.state, branch.zip_code].filter(Boolean);
                const resolvedStatus = this.extractBranchStatus(branch);

                const firstDefined = (...values) => {
                    for (const value of values) {
                        if (value !== undefined && value !== null && value !== "") {
                            return value;
                        }
                    }
                    return null;
                };

                const coerceToNumber = (value) => {
                    if (value === undefined || value === null || value === "") {
                        return 0;
                    }
                    const parsed = Number(value);
                    return Number.isNaN(parsed) ? 0 : parsed;
                };

                const primaryUsers = coerceToNumber(
                    firstDefined(
                        branch.primary_users_count,
                        branch.primary_users,
                        branch.primaryUsers
                    )
                );
                const secondaryUsers = coerceToNumber(
                    firstDefined(
                        branch.secondary_users_count,
                        branch.secondary_users,
                        branch.shared_users,
                        branch.sharedUsers
                    )
                );

                let totalUsers = coerceToNumber(
                    firstDefined(
                        branch.total_users,
                        branch.totalUsers,
                        branch.users_count,
                        branch.total_users_count,
                        branch.totalUsersCount
                    )
                );

                if (totalUsers === 0) {
                    totalUsers = primaryUsers + secondaryUsers;
                }

                const phoneParts = [branch.mobile, branch.landline]
                    .filter((value) => value !== undefined && value !== null && value !== "");

                return {
                    id: branch.id || index,
                    status: resolvedStatus,
                    isActive: resolvedStatus === "active",
                    name: branch.name || `Branch ${branch.id || index + 1}`,
                    address: addressParts.join(", ") || "-",
                    email: branch.email || "-",
                    phone: phoneParts.length ? phoneParts.join(" / ") : "-",
                    stats: {
                        totalUsers,
                        primaryUsers,
                        sharedUsers: secondaryUsers
                    }
                };
            });
        },
        extractBranchStatus(branch = {}) {
            const candidates = [
                branch.status,
                branch.status_label,
                branch.statusLabel,
                branch.status_name,
                branch.statusName,
                branch.status_text,
                branch.statusText,
                branch.status_value,
                branch.statusValue,
                branch.is_active,
                branch.isActive,
            ];

            for (const candidate of candidates) {
                if (candidate === undefined || candidate === null || candidate === "") {
                    continue;
                }

                if (typeof candidate === "object") {
                    const nested = candidate.value ?? candidate.status ?? candidate.label ?? null;
                    if (nested !== null && nested !== undefined && nested !== "") {
                        return this.resolveBranchStatus(nested);
                    }
                    continue;
                }

                return this.resolveBranchStatus(candidate);
            }

            return "active";
        },
        resolveBranchStatus(status) {
            if (status === 1 || status === "1" || status === true) {
                return "active";
            }
            if (status === 0 || status === "0" || status === false) {
                return "inactive";
            }

            if (typeof status === "string") {
                const normalized = status.trim().toLowerCase();

                if (
                    ["inactive", "disabled", "deactivated", "archived"].some((value) =>
                        normalized.includes(value),
                    )
                ) {
                    return "inactive";
                }

                if (
                    ["active", "activated", "enabled", "live", "current"].some((value) =>
                        normalized.includes(value),
                    )
                ) {
                    return "active";
                }

                if (normalized === "0") {
                    return "inactive";
                }
                if (normalized === "1") {
                    return "active";
                }
            }

            return "active";
        },
        cardClass() {
            const base =
                "relative flex h-full flex-col gap-4 rounded border border-slate-200 border-t-transparent bg-white px-6 pt-5 pb-4 shadow-sm";
            return [base];
        },
        statusDotClass(status) {
            return [
                "h-2 w-2 rounded-full",
                status === "active" ? "bg-green-700" : "bg-red-700"
            ];
        },
        statusTextClass(status) {
            return status === "active" ? "text-green-700" : "text-red-700";
        },
        cardAccentClass(status) {
            return status === "active" ? "bg-green-700" : "bg-red-700";
        },
        statusBadgeClass(status) {
            const base =
                "inline-flex items-center gap-1 border px-3 py-1 text-xs font-medium";
            return [base, status === "active" ? "text-green-700" : "text-red-700"];
        },
        statusBadgeStyle(status) {
            return status === "active"
                ? {
                    borderColor: "#6EE7B7",
                    backgroundColor: "#ECFDF5",
                    borderRadius: "3px"
                }
                : {
                    borderColor: "#FECACA",
                    backgroundColor: "#FEF2F2",
                    borderRadius: "3px"
                };
        }
    },
    async mounted() {
        await this.fetchBranches();
    }
};
</script>

<style scoped>
.users-toggle {
    position: relative;
    width: 42px;
    height: 24px;
}

.users-toggle__input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
}

.users-toggle__track {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    width: 42px;
    height: 24px;
    padding: 0 4px;
    border-radius: 9999px;
    background-color: #CBD5E1;
    transition: background-color 0.2s ease;
    box-sizing: border-box;
}

.users-toggle__thumb {
    width: 16px;
    height: 16px;
    border-radius: 9999px;
    background-color: #ffffff;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
    transition: transform 0.2s ease;
    transform: translateX(0);
}

.users-toggle__input:checked+.users-toggle__track {
    background-color: #0398DC;
}

.users-toggle__input:checked+.users-toggle__track .users-toggle__thumb {
    transform: translateX(18px);
}

.users-toggle__input:focus-visible+.users-toggle__track {
    box-shadow: 0 0 0 3px rgba(125, 211, 252, 0.45);
}
</style>
